/*
  Линия долей и спарклайн — вкладка «Подробнее» на «Отчётах» (§3.11.2).

  Перенесено 29.09.2026 из макетов, выбранных владельцем.

  🔴 **Доля в процентах рисуется ОТДЕЛЬНЫМ графиком, а не второй осью поверх
  столбиков долга.** Две оси в одной рамке запрещены: масштабы у рублей и
  процентов разные, и любое сравнение «линия выше столбиков» не означает
  ничего. Человек всё равно прочитает его как сравнение - и сделает вывод из
  совпадения, которого нет.

  🔴 **Рисунок считается в настоящих пикселях** (`useChartWidth`, 30.09.2026):
  условная рамка растягивалась по ширине карточки вместе с подписями, и один и
  тот же шрифт выходил на экране то 7,6 px, то 17,7 px. Замеры - в шапке
  `useChartWidth.js`.
*/

import useChartWidth from './useChartWidth.js'

const EDGE = 40

function anchor(x, width) {
  if (x < EDGE) return { x: 0, textAnchor: 'start' }
  if (x > width - EDGE) return { x: width, textAnchor: 'end' }
  return { x: x.toFixed(1), textAnchor: 'middle' }
}

/*
  `max` - наименьший потолок шкалы, а не предел значений (аудит 26.10.2026, расхождение 13).
  Прежде значение выше потолка обрезалось по нему, и 70 % рисовалось на черте 40 %: линия
  врала о размере. Теперь шкала растёт по данным шагами по 20 пунктов и не выше 100.
*/
const SCALE_STEP = 20

function ceilingFor(rows, floor, average) {
  const peak = Math.max(...rows.map((row) => Number(row.value)), Number(average) || 0, 0)
  return Math.min(100, Math.max(floor, Math.ceil(peak / SCALE_STEP) * SCALE_STEP))
}

export function PercentLine({ rows, every = 0, max: floor = 40, average, height = 190, alt }) {
  const [measure, WIDTH] = useChartWidth()
  const max = ceilingFor(rows, floor, average)
  /*
    Одна точка линии не образует: линия показывает ДВИЖЕНИЕ, а из одного
    значения движения не видно. Такое бывает при узком периоде, и рисовать
    вместо линии точку значило бы показать пустую рамку с подписью.
  */
  if (rows.length < 2) return null

  const padTop = 18
  const padBottom = 28
  // Слева место под подписи шкалы, справа - под надпись о среднем. Без полей и
  // то, и другое ложится поверх самой линии. Правое поле - доля ширины: на
  // узком экране фиксированные 112 пикселей съедали бы четверть графика.
  const padLeft = 34
  const padRight = Math.min(Math.max(WIDTH * 0.16, 90), 150)
  const plot = height - padTop - padBottom
  const step = (WIDTH - padLeft - padRight) / (rows.length - 1)
  const y = (value) => padTop + plot - (Math.min(Number(value), max) / max) * plot

  const points = rows.map((row, index) => [padLeft + index * step, y(row.value)])
  const path = points
    .map(([px, py], index) => `${index ? 'L' : 'M'}${px.toFixed(1)} ${py.toFixed(1)}`)
    .join(' ')

  return (
    <div className="chart__frame" ref={measure}>
      <svg className="chart__svg" viewBox={`0 0 ${WIDTH} ${height}`} role="img" aria-label={alt}>
        {[0, max / 2, max].map((value) => (
          <g key={value}>
            <line
              className="chart__grid"
              x1={padLeft}
              y1={y(value)}
              x2={WIDTH - padRight}
              y2={y(value)}
            />
            <text className="chart__ax" x={padLeft - 6} y={y(value) + 4} textAnchor="end">
              {value}%
            </text>
          </g>
        ))}

        {average === undefined || average === null ? null : (
          <g>
            <line
              className="chart__avg"
              x1={padLeft}
              y1={y(average)}
              x2={WIDTH - padRight}
              y2={y(average)}
            />
            <text className="chart__avg-text" x={WIDTH - padRight + 8} y={y(average) + 4}>
              в среднем {String(average).replace('.', ',')}%
            </text>
          </g>
        )}

        <path className="chart__line" d={path} />

        {rows.map((row, index) => (
          <g key={row.title}>
            <title>
              {row.title}: {row.note}
            </title>
            {/* Прозрачный круг пошире самой точки: попасть курсором в точку
                радиусом два с половиной пикселя человек не может. */}
            <circle cx={points[index][0]} cy={points[index][1]} r="9" fill="transparent" />
            <circle className="chart__dot" cx={points[index][0]} cy={points[index][1]} r="2.5" />
            {every && index % every === 0 ? (
              <text className="chart__ax" {...anchor(points[index][0], WIDTH)} y={height - 10}>
                {row.short ?? row.title}
              </text>
            ) : null}
          </g>
        ))}
      </svg>
    </div>
  )
}

/*
  Спарклайн для плитки: форма ряда без осей и подписей.

  Цвет у него всегда один и тот же, и это осознанно. Зелёный в кабинете
  означает спокойное состояние, а семнадцать процентов собираемости спокойным
  состоянием не являются: цветом здесь не сказано ничего, величину говорит сама
  линия и число рядом с ней.
*/
export function Spark({ values, from, to, alt }) {
  if (values.length < 2) return null

  const width = 240
  const height = 34
  const top = Math.max(...values.map(Number), 0)
  const step = width / (values.length - 1)
  const path = values
    .map((value, index) => {
      const y = height - 2 - (top === 0 ? 0 : (Number(value) / top) * (height - 6))
      return `${index ? 'L' : 'M'}${(index * step).toFixed(1)} ${y.toFixed(1)}`
    })
    .join(' ')

  return (
    <div className="spark__wrap">
      <svg
        className="spark"
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        role="img"
        aria-label={alt}
      >
        <path className="chart__line" d={path} />
      </svg>

      {/*
        🔴 Концы отрезка подписаны (30.09.2026, замечание владельца: полоски «не
        информативны, нужны хотя бы временные отрезки снизу»). Без подписей
        линия показывает форму, но не отвечает на вопрос «за какое время»: тот
        же изгиб за три месяца и за четыре года означает разное.

        Подписи разметкой, а не внутри рисунка: у полоски `preserveAspectRatio`
        снят, она намеренно тянется по ширине плитки - и текст внутри неё
        растянулся бы вместе с линией.
      */}
      {from && to ? (
        <div className="spark__axis">
          <span>{from}</span>
          <span>{to}</span>
        </div>
      ) : null}
    </div>
  )
}
